import { sprite } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';
import { formedBlade, gem, R, ramp, tassel, type Ramp, wrappedGrip } from './craft';

/** Hand-made item pictures: fans, the sword umbrella, bow, crossbow, bladed rings and shields. */

const horn = ramp('#d8c8a0');
const silk = ramp('#efe6d2');
const brass = ramp('#d0a046');

/** A war fan opened round its pivot (cx,cy): leaves in alternating tones, ribs past the leaf. */
function warFan(p: Pix, cx: number, cy: number, r: number, leaf: Ramp, rib: Ramp, a0 = -2.75, a1 = -0.4): void {
  const N = 10;
  for (let k = 0; k < N; k++) {
    const t0 = a0 + ((a1 - a0) * k) / N;
    const t1 = a0 + ((a1 - a0) * (k + 1)) / N;
    const pts: [number, number][] = [
      [cx + Math.cos(t0) * r * 0.35, cy + Math.sin(t0) * r * 0.35],
      [cx + Math.cos(t0) * r, cy + Math.sin(t0) * r],
      [cx + Math.cos(t1) * r, cy + Math.sin(t1) * r],
      [cx + Math.cos(t1) * r * 0.35, cy + Math.sin(t1) * r * 0.35],
    ];
    p.poly(pts, k % 2 ? leaf.d1 : leaf.b);
  }
  for (let k = 0; k <= N; k++) {
    const a = a0 + ((a1 - a0) * k) / N;
    p.line(cx, cy, cx + Math.cos(a) * r * 0.38, cy + Math.sin(a) * r * 0.38, rib.b);
    if (k % 2 === 0) p.px(cx + Math.cos(a) * (r + 1), cy + Math.sin(a) * (r + 1), rib.l1);
  }
  p.ring(cx, cy, r, r, leaf.l1, a0 + 0.9, a1);
  p.ell(cx, cy, 1.6, 1.6, rib.d1);
}

/** A round shield face at (cx,cy): rim, boss, rivets. */
function roundShield(p: Pix, cx: number, cy: number, r: number, face: Ramp, rim: Ramp): void {
  p.ell(cx, cy, r, r, rim.d1);
  p.ell(cx + 0.5, cy - 0.5, r - 1, r - 1, rim.b);
  p.ell(cx, cy, r - 2.5, r - 2.5, face.d1);
  p.ell(cx + 1, cy - 1, r - 4, r - 4, face.b);
  p.ring(cx, cy, r - 0.5, r - 0.5, rim.l2, -1.4, -0.1);
}

registerIcons('item', {
  // พัดเหล็กบุปผาชาด: an iron-ribbed war fan, its leaf painted with cinnabar blossoms.
  'iron-fan': (p) => {
    warFan(p, 9, 25, 19, silk, R.iron);
    for (const [x, y] of [
      [13, 10],
      [20, 13],
      [10, 15],
    ] as const) {
      sprite(p, x - 1, y - 1, ['.r.', 'rgr', '.r.'], { r: R.red.b, g: R.gold.l1 });
    }
    p.line(14, 12, 18, 13, R.ink.b);
    tassel(p, 7, 26, 4);
  },
  // พัดสายลมนิรนาม (legend): a jade-white fan with wind curls lifting off it.
  'u-wind-fan': (p) => {
    warFan(p, 10, 25, 18, ramp('#d8f0e8'), R.jade);
    for (const [x, y, s] of [
      [14, 12, 1],
      [20, 16, -1],
    ] as const) {
      p.ring(x, y, 3, 2, R.jade.d1, s > 0 ? Math.PI : 0, s > 0 ? Math.PI * 2.3 : Math.PI * 1.3);
    }
    // Wind trails leaving the fan's edge.
    p.line(23, 6, 29, 4, R.jade.l2).line(26, 10, 30, 9, R.jade.l1).line(27, 15, 30, 15, R.jade.l2);
    gem(p, 10, 25, 2, R.jade);
    tassel(p, 9, 27, 3, R.jade);
  },
  // ร่มซ่อนกระบี่: a closed oil-paper umbrella whose handle is a sword grip, blade peeking out.
  'sword-umbrella': (p) => {
    // The furled canopy: a long cone, folds in two tones.
    p.poly(
      [
        [9, 23],
        [12, 15],
        [25, 3],
        [27, 5],
        [17, 18],
      ],
      R.lacquer.b,
    );
    p.line(12, 17, 24, 5, R.lacquer.l1);
    p.line(15, 19, 25, 6, R.lacquer.d1);
    p.px(26, 4, R.lacquer.l2);
    // Tie band, ferrule.
    p.thick(15, 17, 17, 19, R.gold.b, 2);
    p.line(27, 3, 30, 1, R.gold.d1);
    // The tip of the hidden blade showing at the base.
    formedBlade(p, 11, 21, 15, 17, R.steel, { w: 2.2, ridge: false });
    // Grip as a sword hilt.
    p.thick(8, 24, 11, 21, R.gold.d1, 2);
    wrappedGrip(p, 3, 29, 8, 24, R.ink);
    p.px(2, 30, R.gold.l1);
    tassel(p, 4, 27, 3);
  },
  // ธนูเขาสัตว์ลงรัก: a horn-and-lacquer recurve bow, strung.
  'horn-bow': (p) => {
    const limb: [number, number][] = [
      [8, 3],
      [11, 5],
      [16, 9],
      [20, 14],
      [22, 19],
      [22, 24],
      [24, 28],
    ];
    p.path(limb, R.lacquer.d1, 3);
    p.path(limb, R.lacquer.b, 2);
    p.path(
      limb.map(([x, y]) => [x + 1, y - 1] as [number, number]),
      horn.b,
    );
    // Recurved horn tips.
    p.thick(8, 3, 5, 4, horn.l1, 2).thick(24, 28, 26, 30, horn.l1, 2);
    // Grip.
    p.thick(18, 12, 21, 16, R.wood.l1, 3);
    p.px(20, 13, R.gold.l2);
    // String.
    p.line(6, 5, 25, 29, silk.l1);
    // An arrow nocked.
    p.line(5, 27, 27, 6, R.wood.b);
    p.poly(
      [
        [26, 5],
        [29, 3],
        [27, 7],
      ],
      R.steel.l1,
    );
    p.line(4, 26, 6, 29, R.red.b).line(3, 27, 5, 30, R.red.d1);
  },
  // หน้าไม้กลไกสามดอก: a repeating crossbow, magazine box on the stock, three bolts loaded.
  'repeating-crossbow': {
    draw: (p) => {
      // Along the stock: u forward (up-right), v across (+ down-right).
      const at = (u: number, v: number): [number, number] => [4 + u * 0.707 + v * 0.707, 28 - u * 0.707 + v * 0.707];
      p.thick(4, 28, 21, 11, R.wood.d1, 3);
      p.line(3, 27, 20, 10, R.wood.l1);
      // Trigger and butt.
      p.thick(...at(9, 2), ...at(9, 5), R.iron.d1, 2);
      p.thick(...at(-1, -2), ...at(-1, 2), R.wood.d2, 2);
      // The magazine box riding on the stock, three bolt heads leaving its front.
      p.poly([at(7, -1.5), at(18, -1.5), at(18, -6), at(7, -6)], R.wood.b);
      p.path([at(7, -6), at(18, -6)], R.wood.l2);
      p.path([at(9, -3.5), at(16, -3.5)], R.wood.d1);
      for (const v of [-2.2, -3.8, -5.4]) {
        p.line(...at(18, v), ...at(21, v), R.steel.b);
        p.px(...at(22, v), R.steel.l2);
      }
      // The lever arching back over the box.
      p.path([at(8, -6), at(5, -10), at(13, -11)], R.iron.d1);
      p.px(...at(13, -11), R.iron.l2);
      // The prod across the front, bowed forward, and its string.
      p.path([at(19, -8), at(22, -5), at(23, 0), at(22, 5), at(19, 8)], R.lacquer.d1, 2);
      p.path([at(19.5, -8), at(22.5, -4.5), at(23.5, 0)], R.lacquer.l1);
      p.line(...at(19, -8), ...at(19, 8), silk.d1);
      p.px(...at(19, -8), R.gold.l1).px(...at(19, 8), R.gold.l1);
    },
    // At 16 the box and lever blur: just the stock, the bowed prod and its string, one bolt.
    // At 16 it stands upright (the classic crossbow T) so the bow and string read: stock, the
    // magazine box, the bowed prod and string, one bolt head.
    draw16: (p) => {
      p.rect(7, 5, 2, 10, R.wood.d1);
      p.line(8, 5, 8, 13, R.wood.l1);
      p.rect(6, 13, 4, 2, R.wood.d2);
      p.px(9, 11, R.iron.l1);
      p.rect(6, 7, 4, 3, R.wood.b);
      p.line(6, 7, 9, 7, R.wood.l2);
      p.line(2, 8, 14, 8, silk.d1);
      p.path(
        [
          [2, 8],
          [3, 6],
          [6, 4],
          [8, 4],
          [10, 4],
          [13, 6],
          [14, 8],
        ],
        R.lacquer.b,
      );
      p.line(9, 4, 12, 5, R.lacquer.l1);
      p.px(2, 8, R.gold.l1).px(14, 8, R.gold.l1);
      p.line(8, 2, 8, 4, R.steel.b);
      p.px(8, 1, R.steel.l2);
    },
  },
  // ห่วงใบมีดคู่: a pair of steel rings each set with crescent blades, one behind the other.
  'bladed-rings': (p) => {
    const ring = (cx: number, cy: number, r: number, m: Ramp, spin: number) => {
      for (let k = 0; k < 4; k++) {
        const a = spin + (k * Math.PI) / 2;
        p.poly(
          [
            [cx + Math.cos(a - 0.45) * (r - 1), cy + Math.sin(a - 0.45) * (r - 1)],
            [cx + Math.cos(a + 0.3) * (r + 5), cy + Math.sin(a + 0.3) * (r + 5)],
            [cx + Math.cos(a + 0.05) * (r + 2), cy + Math.sin(a + 0.05) * (r + 2)],
            [cx + Math.cos(a + 0.4) * (r - 1), cy + Math.sin(a + 0.4) * (r - 1)],
          ],
          m.b,
        );
        p.px(cx + Math.cos(a + 0.3) * (r + 5), cy + Math.sin(a + 0.3) * (r + 5), m.l2);
      }
      p.ring(cx, cy, r, r, m.d1);
      p.ring(cx, cy, r - 1, r - 1, m.b);
      p.ring(cx, cy, r - 1, r - 1, m.l2, -1.6, -0.1);
      // The hand grip: a red-wrapped bar across the inside.
      p.thick(cx - r * 0.55, cy + r * 0.3, cx + r * 0.1, cy + r * 0.75, R.red.b, 2);
      p.px(cx - r * 0.3, cy + r * 0.45, R.red.l1);
    };
    ring(21, 11, 6, R.iron, 0.4);
    ring(13, 19, 7, R.steel, 0);
  },
  // โล่กลมทองเหลือง: a round brass shield with a raised boss and a ring of rivets.
  'bronze-shield': (p) => {
    roundShield(p, 16, 16, 13, brass, R.bronze);
    for (let k = 0; k < 8; k++) {
      const a = (k * Math.PI) / 4;
      p.px(16 + Math.cos(a) * 8.5, 16 + Math.sin(a) * 8.5, brass.d2);
    }
    gem(p, 16, 16, 3.6, R.bronze);
    p.ring(16, 16, 5, 5, brass.d1, 0.6, 2.6);
  },
  // โล่หน้าอสูร: a dark-lacquer shield bearing a snarling bronze beast face.
  'beast-shield': (p) => {
    roundShield(p, 16, 16, 13, R.ink, R.lacquer);
    // The beast face: horns, brows, eyes, fanged mouth (a symmetric stamp).
    sprite(
      p,
      7,
      8,
      [
        'h................h',
        '.hh............hh.',
        '..bbbbb....bbbbb..',
        '.bbBBbbbbbbbbBBbb.',
        '.bbeEbbbbbbbbeEbb.',
        '..bbbbbbBBbbbbbb..',
        '...bbbbbBBbbbbb...',
        '..bdddddddddddb...',
        '..bdfdddddddfdb...',
        '...bbbbbbbbbbbb...',
        '.....bb....bb.....',
      ],
      {
        h: R.gold.l1,
        b: R.bronze.b,
        B: R.bronze.l2,
        e: C.blood,
        E: C.fireLight,
        d: R.bronze.d2,
        f: R.paper.l2,
      },
    );
    p.px(17, 21, R.gold.l1).px(14, 21, R.gold.l1);
  },
});
